import type { CostQuality, CurrentCostStatus } from '../types/otherSales';

// Fase 22C-3: presentación pura del estado de costeo. Extraída para probar sin renderizador. El texto
// acompaña al tono (accesibilidad). El margen solo es válido cuando la calidad es CALCULATED.
export type CostTone = 'entry' | 'out' | 'neutral' | 'warn';

/** P-071: la nota única con la que se declara una cifra aproximada (misma frase que el servidor). */
export const APPROXIMATE_COST_NOTE = 'Parte del costo se basa en valores aproximados.';

export interface CostSummary {
  label: string;
  tone: CostTone;
  hint: string;
  /** true solo cuando el margen mostrado es completo y confiable (exacto). */
  marginReliable: boolean;
  /** P-071: 'reliable' = margen exacto; 'approximate' = margen completo pero aproximado (se muestra, dicho así); 'none' = no se muestra. */
  margin: 'reliable' | 'approximate' | 'none';
}

export function costSummary(quality: CostQuality): CostSummary {
  switch (quality) {
    case 'CALCULATED':
      return { label: 'Costo calculado', tone: 'out', hint: 'Costo histórico completo; margen confiable.', marginReliable: true, margin: 'reliable' };
    case 'APPROXIMATE':
      return { label: 'Costo aproximado', tone: 'warn', hint: `${APPROXIMATE_COST_NOTE} El margen es una estimación, no un dato exacto.`, marginReliable: false, margin: 'approximate' };
    case 'PARTIAL':
      return { label: 'Costo incompleto', tone: 'warn', hint: 'Falta el costo de al menos un insumo; el margen no se muestra para no ser engañoso.', marginReliable: false, margin: 'none' };
    case 'UNAVAILABLE':
      return { label: 'Costo no disponible', tone: 'warn', hint: 'La venta debería tener costo, pero el movimiento no lo registró.', marginReliable: false, margin: 'none' };
    case 'NO_RECIPE':
      return { label: 'Producto sin costo definido', tone: 'neutral', hint: 'No existe costo calculable porque el producto se vendió sin receta y sin costo aproximado.', marginReliable: false, margin: 'none' };
    case 'HISTORICAL_UNAVAILABLE':
      return { label: 'Venta histórica sin costo', tone: 'neutral', hint: 'Esta venta fue registrada antes de habilitarse el costeo histórico.', marginReliable: false, margin: 'none' };
    case 'NOT_APPLICABLE':
      return { label: 'Sin líneas de producto', tone: 'neutral', hint: 'Registro sin líneas de producto; no aplica costeo.', marginReliable: false, margin: 'none' };
    default:
      // P-074: un estado que esta versión no conoce (lo manda un servidor más nuevo) no tumba la pantalla.
      // El APK de P-070 no tenía APPROXIMATE, devolvía undefined y caía en el ErrorBoundary (P-073). Lo
      // conservador: sin margen, y dicho.
      return { label: 'Costo no disponible', tone: 'warn', hint: 'Esta versión de la app no reconoce el estado del costo; el margen no se muestra.', marginReliable: false, margin: 'none' };
  }
}

/**
 * P-074: la calidad con la que se presenta el «Costo estimado actual» (`CurrentCostStatus`). Lo que esta versión
 * no conoce se presenta como no disponible: sin margen (revisión independiente de P-074, R-02).
 */
export function currentCostQuality(status: CurrentCostStatus): CostQuality {
  switch (status) {
    case 'COMPLETE': return 'CALCULATED';
    case 'APPROXIMATE': return 'APPROXIMATE';
    case 'NO_RECIPE': return 'NO_RECIPE';
    case 'PARTIAL': return 'PARTIAL';
    default: return 'UNAVAILABLE';
  }
}

// Formato monetario simple con la moneda del negocio (el backend ya envía el string con 2 decimales).
export function formatCostMoney(amount: string | null, currency: string): string {
  if (amount === null) return '—';
  return `${amount} ${currency}`;
}
